import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import {
  CHAT_PAGE_SIZE,
  MESSAGES_PAGE_SIZE,
  PRESENCE_POLL_MS,
} from '../config/constants';
import {
  buildConversationListFilter,
  chatListIdentity,
  folderScope,
  type ChatListFilter,
} from '../lib/list-filter';
import type {
  ChatStatusTab,
  ConversationFolder,
  ConversationId,
  MacroLang,
} from '../model/schema';
import { conversationsApi } from './conversations-api';

/** The URL-driven inputs of the chat list (everything but the page). */
export interface ConversationListParams {
  folder: ConversationFolder;
  status: ChatStatusTab;
  search?: string | undefined;
  urgent?: boolean | undefined;
}

/**
 * Query-key factory for the `conversation` domain. Anything that mutates a chat
 * invalidates through `conversationsKeys`, never an inline array (CLAUDE.md).
 * The list key carries the wire filter WITHOUT its `skip`, so every page of one
 * filter shares a cache entry (an infinite query).
 */
export const conversationsKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationsKeys.all, 'list'] as const,
  list: (filter: Omit<ChatListFilter, 'skip'>) =>
    [...conversationsKeys.lists(), filter] as const,
  details: () => [...conversationsKeys.all, 'detail'] as const,
  detail: (id: ConversationId) => [...conversationsKeys.details(), String(id)] as const,
  /** Every message window of every chat. */
  threads: () => [...conversationsKeys.all, 'messages'] as const,
  /** Every message window of one chat. */
  thread: (id: ConversationId) => [...conversationsKeys.threads(), String(id)] as const,
  messages: (id: ConversationId, pageCount: number) =>
    [...conversationsKeys.thread(id), pageCount] as const,
  notes: (id: ConversationId) => [...conversationsKeys.all, 'notes', String(id)] as const,
  macros: () => [...conversationsKeys.all, 'macros'] as const,
  macrosByLang: (lang: MacroLang) => [...conversationsKeys.macros(), lang] as const,
  presence: (userId: ConversationId) =>
    [...conversationsKeys.all, 'presence', String(userId)] as const,
  courier: (id: ConversationId) =>
    [...conversationsKeys.all, 'courier', String(id)] as const,
  operators: () => [...conversationsKeys.all, 'operators'] as const,
};

/** The list filter minus `skip` — the cache identity of an infinite list. */
function listIdentity(params: ConversationListParams): Omit<ChatListFilter, 'skip'> {
  return chatListIdentity(buildConversationListFilter({ ...params, pageCount: 0 }));
}

export const conversationsQueries = {
  /**
   * The chat list as an infinite query. Page `n` asks for `skip = 15 * n`
   * (cpanel `pageCount`); the "my chats" view is never paged. More pages exist
   * while fewer than `ceil(count / 15)` have been loaded (cpanel's
   * `current_page < ceil(count / SIZE)`).
   */
  list: (params: ConversationListParams) =>
    infiniteQueryOptions({
      queryKey: conversationsKeys.list(listIdentity(params)),
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        conversationsApi.list(
          buildConversationListFilter({ ...params, pageCount: pageParam }),
        ),
      getNextPageParam: (lastPage, allPages) =>
        folderScope(params.folder).myChats ||
        allPages.length >= Math.ceil(lastPage.count / CHAT_PAGE_SIZE)
          ? undefined
          : allPages.length,
    }),

  detail: (id: ConversationId) =>
    queryOptions({
      queryKey: conversationsKeys.detail(id),
      queryFn: () => conversationsApi.getById(id),
    }),

  /** The newest `15 × pageCount` messages of a chat. */
  messages: (id: ConversationId, pageCount: number) =>
    queryOptions({
      queryKey: conversationsKeys.messages(id, pageCount),
      queryFn: () => conversationsApi.messages(id, MESSAGES_PAGE_SIZE * pageCount),
      placeholderData: keepPreviousData,
    }),

  notes: (id: ConversationId) =>
    queryOptions({
      queryKey: conversationsKeys.notes(id),
      queryFn: () => conversationsApi.notes(id),
    }),

  macros: (lang: MacroLang) =>
    queryOptions({
      queryKey: conversationsKeys.macrosByLang(lang),
      queryFn: () => conversationsApi.macros(lang),
    }),

  presence: (userId: ConversationId, role: string | undefined) =>
    queryOptions({
      queryKey: conversationsKeys.presence(userId),
      queryFn: () => conversationsApi.presence(userId, role),
      refetchInterval: PRESENCE_POLL_MS,
    }),

  courier: (id: ConversationId) =>
    queryOptions({
      queryKey: conversationsKeys.courier(id),
      queryFn: () => conversationsApi.courier(id),
    }),

  operators: () =>
    queryOptions({
      queryKey: conversationsKeys.operators(),
      queryFn: () => conversationsApi.operators(),
    }),
};
